import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams } from "react-router-dom";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import {
  useGetEventQuery,
  useGetEventTicketsQuery,
} from "../../../redux/features/event/event.api";
import { CreatedTicket } from "../../../redux/features/event/event.types";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../utils/constants";
import { TicketPurchase } from "../../../utils/types";
import { PreviewStep } from "./previewStep";
import { PurchaseTickets } from "./purchase";

const PreviewLoader = () => {
  return (
    <div className="flex flex-col gap-3 md:flex-row md:gap-10">
      <div className="flex w-full flex-col gap-3 md:max-w-[500px]">
        <BgWrapper className="h-[300px] w-full md:h-[472px]">
          <Skeleton
            count={1}
            width={"100%"}
            height={"100%"}
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
        </BgWrapper>
      </div>

      <div className="flex flex-1 flex-col gap-3">
        <BgWrapper className="flex w-full flex-col gap-1">
          <Skeleton
            count={1}
            width={"100%"}
            height={"40px"}
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
          <Skeleton
            count={1}
            width={"90%"}
            height={"40px"}
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
          <Skeleton
            count={1}
            width={"80%"}
            height={"40px"}
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
        </BgWrapper>
        <BgWrapper className="h-[70px] w-full">
          <Skeleton
            count={1}
            width={"100%"}
            height={"100%"}
            baseColor={SKELETON_BASE_COLOR}
            highlightColor={SKELETON_HIGHLIGHT_COLOR}
          />
        </BgWrapper>
      </div>
    </div>
  );
};

export const PreviewSingleEvent = ({ canBuy = false }: { canBuy: boolean }) => {
  const { slug } = useParams<{ slug: string }>();

  const navigate = useNavigate();

  const [ticketsPurchase, setTicketsPurchase] = useState<TicketPurchase[]>([]);
  const [individualTicketsPurchase, setIndividualTicketsPurchase] = useState<
    CreatedTicket[]
  >([]);
  const [step, setStep] = useState<1 | 2>(1);

  const {
    data: eventResponse,
    isFetching: fetchingEvent,
    isError: eventFetchError,
  } = useGetEventQuery(
    {
      slug: slug,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: tickets, isFetching: fetchingTickets } =
    useGetEventTicketsQuery(
      {
        id: eventResponse?.data._id ?? "",
      },
      { refetchOnMountOrArgChange: true, skip: fetchingEvent },
    );

  useEffect(() => {
    const ticketPurchaseBucket = ticketsPurchase.map((tp) => {
      return Array.from({ length: tp.quantity }, (_, i) => tp.ticket);
    });

    setIndividualTicketsPurchase(ticketPurchaseBucket.flat(1));
  }, [ticketsPurchase]);

  useEffect(() => {
    if (
      (canBuy && eventResponse?.data?.status === "draft") ||
      eventResponse?.data.status === "completed" ||
      eventFetchError
    ) {
      navigate("/404");
    }
    // eslint-disable-next-line
  }, [eventResponse]);

  return fetchingEvent || fetchingTickets ? (
    <PreviewLoader />
  ) : (
    <>
      {eventResponse?.data !== undefined && step === 1 ? (
        <PreviewStep
          event={eventResponse?.data!}
          tickets={tickets?.data?.tickets ?? []}
          ticketsPurchase={ticketsPurchase}
          setTicketsPurchase={setTicketsPurchase}
          setStep={setStep}
          canBuy={canBuy}
        />
      ) : undefined}

      {eventResponse?.data !== undefined && step === 2 ? (
        <PurchaseTickets
          event={eventResponse?.data!}
          tickets={tickets?.data?.tickets ?? []}
          ticketsPurchase={ticketsPurchase}
          setTicketsPurchase={setTicketsPurchase}
          setStep={setStep}
          individualTicketsPurchase={individualTicketsPurchase}
        />
      ) : undefined}
    </>
  );
};
